import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getRequestPhase } from '../../../api'
import {
  EMPTY_LIST,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import { useAsyncCallback, useNotification } from '../../../hooks'
import { getRequestStatuses, RequestStatusSelectInfo } from '../../Request'
import { ButtonSelect } from '../../Select'
import {
  isRequestCloseConfirmationDisabled,
  RequestCloseConfirmationModal,
} from '../RequestCloseConfirmationModal'
import { useRequestProfileContext } from '../RequestProfile.hooks'
import { RequestProfileHeaderStatusSelectButton } from '../RequestProfileHeaderStatusSelectButton'

import {
  REQUEST_PROFILE_HEADER_STATUS_SELECT_DROPDOWN_MAX_HEIGHT,
  REQUEST_PROFILE_HEADER_STATUS_SELECT_QA_NAME,
} from './RequestProfileHeaderStatusSelect.constants'
import {
  getRequestProfileHeaderStatusSelectOptions,
  getRequestProfileHeaderStatusSelectValue,
  hasRequestProfileUndoneActivities,
  isRequestTerminalStatus,
} from './RequestProfileHeaderStatusSelect.utils'

import * as styles from './RequestProfileHeaderStatusSelect.module.css'

function RequestProfileHeaderStatusSelect({ className, disabled, isLoading }) {
  const { t } = useTranslation()
  const { handleAddErrorNotification } = useNotification()

  const { handleCloseModal, handleUpdateEntity, requestData, setModal } =
    useRequestProfileContext()

  const [options, setOptions] = useState(EMPTY_LIST)
  const [isStatusUpdating, setStatusUpdating] = useState(false)
  const currentStatusId = requestData?.status?.id

  const handleLoadStatusOptions = useAsyncCallback(() => {
    return getRequestStatuses()
      .then(({ data }) => {
        setOptions(data?.results?.objects || EMPTY_LIST)
      })
      .catch(() => {
        setOptions(EMPTY_LIST)
      })
  }, [])

  useEffect(() => {
    handleLoadStatusOptions()
  }, [])

  function handleUpdateStatus(nextStatusId) {
    setStatusUpdating(true)

    return handleUpdateEntity({ status: nextStatusId }).finally(() => {
      setStatusUpdating(false)
    })
  }

  const handleLoadRequestPipeline = useAsyncCallback(() => {
    return getRequestPhase({ id: requestData?.id })
      .then(({ data }) => data)
      .catch(error => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')

        throw error
      })
  }, [handleAddErrorNotification, requestData?.id])

  function handleSubmitTerminalStatus(nextStatusId) {
    return handleUpdateStatus(nextStatusId).then(() => {
      handleCloseModal()
    })
  }

  function handleOpenCloseConfirmationModal(nextStatusId) {
    function handleSubmitCloseConfirmation() {
      return handleSubmitTerminalStatus(nextStatusId)
    }

    setModal(
      <RequestCloseConfirmationModal
        onCancel={handleCloseModal}
        onSubmit={handleSubmitCloseConfirmation}
      />
    )
  }

  function handleSelectTerminalStatus(nextStatusId) {
    if (isRequestCloseConfirmationDisabled()) {
      return handleUpdateStatus(nextStatusId)
    }

    return handleLoadRequestPipeline()
      .then(pipeline => {
        const shouldShowCloseConfirmation =
          hasRequestProfileUndoneActivities(pipeline)

        if (shouldShowCloseConfirmation) {
          handleOpenCloseConfirmationModal(nextStatusId)

          return null
        }

        return handleUpdateStatus(nextStatusId)
      })
      .catch(console.error)
  }

  function handleSelectStatus(nextStatus) {
    const nextStatusId = nextStatus.id
    const statusChanged = nextStatusId !== currentStatusId

    if (statusChanged) {
      if (isRequestTerminalStatus(nextStatusId)) {
        return handleSelectTerminalStatus(nextStatusId)
      }

      return handleUpdateStatus(nextStatusId)
    }

    return null
  }

  const preparedOptions = getRequestProfileHeaderStatusSelectOptions(options, t)
  const selectedStatus = requestData?.status
    ? getRequestProfileHeaderStatusSelectValue(requestData.status, t)
    : null

  const isSelectDisabled =
    disabled || isLoading || isStatusUpdating || preparedOptions.length === 0

  return (
    <ButtonSelect
      Control={RequestProfileHeaderStatusSelectButton}
      className={className}
      controlProps={{
        isSkeleton: isLoading,
      }}
      disabled={isSelectDisabled}
      dropdownFloatingConfig={{
        dropdownPlacement: FLOATING_ELEMENT_PLACEMENT.bottom,
        dropdownStyles: {
          maxHeight: REQUEST_PROFILE_HEADER_STATUS_SELECT_DROPDOWN_MAX_HEIGHT,
        },
        maxWidthByRef: false,
      }}
      dropdownProps={{
        className: styles.dropdown,
      }}
      listProps={{
        headerProps: {
          Search: null,
        },
        itemProps: {
          contentProps: {
            Info: RequestStatusSelectInfo,
          },
        },
      }}
      options={preparedOptions}
      qaName={REQUEST_PROFILE_HEADER_STATUS_SELECT_QA_NAME}
      value={selectedStatus}
      onSelectItem={handleSelectStatus}
    />
  )
}

export default RequestProfileHeaderStatusSelect
